import Icons from './Icons.tsx';
import {
  Box, Typography, Slider, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress, Stack,
  InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel, FormGroup, RadioGroup, Radio, Switch
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import { useState, useCallback, useEffect } from 'react';
import { useTagsRegistry } from './TagsProvider.tsx';

interface MapPreferencesProps {
  preferences: { [key: string]: any };
  setPreferences: (prefs: { [key: string]: any }) => void;
  user: any;
  displayedUser: any;
  onSaveConfig: (key: string, value: any, valueType: 'int' | 'float' | 'string' | 'bool') => Promise<any>;
}

const materialPalette = [
  '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5',
  '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
  '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800',
  '#ff5722', '#795548', '#607d8b', '#000000', '#ffffff'
];

// Color conversion utilities
const hexToHsl = (hex: string): [number, number, number] => {
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
};

const hslToHex = (h: number, s: number, l: number): string => {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return toHex(r) + toHex(g) + toHex(b);
};

// Custom Color Picker Component
const CustomColorPicker: React.FC<{
  value: string;
  onChange: (color: string) => void;
  onClose: () => void;
  onBack?: () => void;
}> = ({ value, onChange, onClose, onBack }) => {
  const [hsl, setHsl] = useState<[number, number, number]>(() => hexToHsl(value));

  useEffect(() => {
    setHsl(hexToHsl(value));
  }, [value]);

  const handleHueChange = useCallback((_: Event, newHue: number | number[]) => {
    const hue = Array.isArray(newHue) ? newHue[0] : newHue;
    const newHsl: [number, number, number] = [hue, hsl[1], hsl[2]];
    setHsl(newHsl);
    onChange(hslToHex(newHsl[0], newHsl[1], newHsl[2]));
  }, [hsl, onChange]);

  const handleSaturationBrightnessChange = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));

    const newHsl: [number, number, number] = [hsl[0], x * 100, (1 - y) * 100];
    setHsl(newHsl);
    onChange(hslToHex(newHsl[0], newHsl[1], newHsl[2]));
  }, [hsl, onChange]);

  const currentColor = hslToHex(hsl[0], hsl[1], hsl[2]);

  return (
    <Box sx={{ p: 2, width: '100%' }}>
      {/* Current Color Display */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            backgroundColor: `#${currentColor}`,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}
        />
        <TextField
          value={`#${currentColor}`}
          onChange={(e) => {
            const hex = e.target.value.replace(/^#/, '');
            if (/^[0-9a-fA-F]{6}$/.test(hex)) {
              onChange(hex);
            }
          }}
          size="small"
          sx={{ flex: 1 }}
        />
      </Box>

      {/* 2D Saturation/Brightness Matrix */}
      <Box
        sx={{
          width: '100%',
          height: 200,
          background: `hsl(${hsl[0]}, 100%, 50%)`,
          backgroundImage: `
            linear-gradient(to right, #fff 0%, transparent 100%),
            linear-gradient(to top, #000 0%, transparent 100%)
          `,
          borderRadius: 1,
          cursor: 'crosshair',
          position: 'relative',
          mt: 2
        }}
        onClick={handleSaturationBrightnessChange}
      >
        {/* Color indicator */}
        <Box
          sx={{
            position: 'absolute',
            left: `${hsl[1]}%`,
            top: `${100 - hsl[2]}%`,
            width: 12,
            height: 12,
            borderRadius: '50%',
            border: '2px solid white',
            boxShadow: '0 0 0 1px black',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none'
          }}
        />
      </Box>

      {/* Hue Slider */}
      <Box
        sx={{
          width: '100%',
          height: 20,
          background: 'linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)',
          borderRadius: 1,
          position: 'relative',
          mt: 2
        }}
      >
        <Slider
          value={hsl[0]}
          onChange={handleHueChange}
          min={0}
          max={360}
          step={1}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            '& .MuiSlider-thumb': {
              backgroundColor: 'white',
              border: '2px solid black',
              width: 16,
              height: 16,
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(255, 255, 255, 0.16)'
              }
            },
            '& .MuiSlider-rail': {
              opacity: 0
            },
            '& .MuiSlider-track': {
              opacity: 0
            }
          }}
        />
      </Box>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          {onBack && (
            <Button variant="outlined" onClick={onBack}>Presets</Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={onClose}>OK</Button>
        </Box>
      </Box>
    </Box>
  );
};

const ColorPicker: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  dialogTitle?: string;
  sx?: any;
}> = ({ label, value, onChange, dialogTitle, sx }) => {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setShowCustom(false);
  };
  const handleSelect = (color: string) => {
    onChange(color.replace(/^#/, ''));
    setOpen(false);
    setShowCustom(false);
  };

  return (
    <>
      <TextField
        type="text"
        label={label}
        value={value}
        onChange={(e) => {
          const val = e.target.value.replace(/^#/, '');
          if (/^[0-9a-fA-F]*$/.test(val) && val.length <= 6) {
            onChange(val);
          }
        }}
        inputProps={{ maxLength: 6, pattern: '[0-9a-fA-F]*' }}
        fullWidth
        sx={sx}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton onClick={handleOpen} edge="start" sx={{ p: 0.5, ml: 0 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: `#${value}`,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                />
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth={false}
        sx={{ '& .MuiDialog-paper': { width: 'min(100%, 400px)' } }}
      >
        <DialogTitle>{dialogTitle || label}</DialogTitle>
        <DialogContent sx={{ p: 0, width: '100%' }}>
          {!showCustom ? (
            /* Material Palette */
            <Box sx={{ p: 2, width: '100%' }}>
              <Box sx={{ display: 'grid', gridTemplateRows: 'repeat(4, minmax(0, 1fr))', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gridAutoFlow: 'column', gap: 1, mb: 2, width: '100%' }}>
                {materialPalette.map((color) => (
                  <Box
                    key={color}
                    onClick={() => handleSelect(color)}
                    sx={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      bgcolor: color,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: color.toLowerCase() === `#${value.toLowerCase()}` ? '3px solid' : '1px solid',
                      borderColor: color.toLowerCase() === `#${value.toLowerCase()}` ? 'primary.main' : 'divider'
                    }}
                  />
                ))}
              </Box>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<ColorLensIcon />}
                    onClick={() => setShowCustom(true)}
                  >
                    Custom
                  </Button>
                </Box>
                <Box>
                  <Button
                    variant="text"
                    fullWidth
                    onClick={handleClose}
                  >
                    Close
                  </Button>
                </Box>
              </Box>
            </Box>
          ) : (
            /* Custom Color Picker */
            <CustomColorPicker
              value={value}
              onChange={(color) => onChange(color)}
              onClose={handleClose}
              onBack={() => setShowCustom(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const MapPreferences: React.FC<MapPreferencesProps> = ({
  preferences,
  setPreferences,
  user,
  displayedUser,
  onSaveConfig
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const tagsCtx = useTagsRegistry();

  const handleSmoothingChange = (value: number) => {
    setPreferences({ ...preferences, smoothingFactor: value });
  };

  const handlePathTypeChange = (value: string) => {
    setPreferences({ ...preferences, pathType: value });
  };

  const handlePathColorChange = (value: string) => {
    setPreferences({ ...preferences, pathColor: value });
  };

  const handlePathOpacityChange = (value: number) => {
    setPreferences({ ...preferences, pathOpacity: value });
  };

  const handlePathWeightChange = (value: number) => {
    setPreferences({ ...preferences, pathWeight: value });
  };

  const handlePathDashChange = (value: string) => {
    setPreferences({ ...preferences, pathDash: value });
  };

  const handleMarkerTypeChange = (value: string) => {
    setPreferences({ ...preferences, markerType: value });
  };

  const handleMarkerRadiusChange = (value: number) => {
    setPreferences({ ...preferences, markerRadius: value });
  };

  const handleMarkerFillTypeChange = (value: string) => {
    setPreferences({ ...preferences, markerFillType: value });
  };

  const handleMarkerFillColorChange = (value: string) => {
    setPreferences({ ...preferences, markerFillColor: value });
  };

  const handleMarkerFillOpacityChange = (value: number) => {
    setPreferences({ ...preferences, markerFillOpacity: value });
  };

  const handleMarkerBorderTypeChange = (value: string) => {
    setPreferences({ ...preferences, markerBorderType: value });
  };

  const handleMarkerBorderColorChange = (value: string) => {
    setPreferences({ ...preferences, markerBorderColor: value });
  };

  const handleMarkerBorderOpacityChange = (value: number) => {
    setPreferences({ ...preferences, markerBorderOpacity: value });
  };

  const handleMarkerBorderWeightChange = (value: number) => {
    setPreferences({ ...preferences, markerBorderWeight: value });
  };

  const handleMarkerBorderDashChange = (value: string) => {
    setPreferences({ ...preferences, markerBorderDash: value });
  };

  const handlePathShadeChildrenChange = (value: boolean) => {
    setPreferences({ ...preferences, pathShadeChildren: value });
  };

  const handleMarkerShadeChildrenChange = (value: boolean) => {
    setPreferences({ ...preferences, markerShadeChildren: value });
  };

  const handleFilterProviderChange = (provider: string, checked: boolean) => {
    const current = preferences.filterProvider || [];
    const updated = checked
      ? [...current, provider]
      : current.filter((p: string) => p !== provider);
    setPreferences({ ...preferences, filterProvider: updated });
  };

  const handleFilterSourceChange = (source: string, checked: boolean) => {
    const current = preferences.filterSource || [];
    const updated = checked
      ? [...current, source]
      : current.filter((s: string) => s !== source);
    setPreferences({ ...preferences, filterSource: updated });
  };

  const handleFilterTravelModeChange = (mode: string, checked: boolean) => {
    const current = preferences.filterTravelMode || [];
    const updated = checked
      ? [...current, mode]
      : current.filter((m: string) => m !== mode);
    setPreferences({ ...preferences, filterTravelMode: updated });
  };

  const handleNumericFilterModeChange = (filterName: string, mode: string) => {
    setPreferences({ ...preferences, [`${filterName}Mode`]: mode });
  };

  const handleSavePreferences = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await Promise.all([
        onSaveConfig('path_options.smoothing_factor', preferences.smoothingFactor, 'float'),
        onSaveConfig('path_options.path_type', preferences.pathType, 'string'),
        onSaveConfig('path_options.path_color', preferences.pathColor, 'string'),
        onSaveConfig('path_options.path_opacity', preferences.pathOpacity, 'float'),
        onSaveConfig('path_options.path_weight', preferences.pathWeight, 'int'),
        onSaveConfig('path_options.path_dash', preferences.pathDash, 'string'),
        onSaveConfig('path_options.path_shade_children', preferences.pathShadeChildren, 'bool'),
        onSaveConfig('path_options.marker_type', preferences.markerType, 'string'),
        onSaveConfig('path_options.marker_radius', preferences.markerRadius, 'int'),
        onSaveConfig('path_options.marker_fill_type', preferences.markerFillType, 'string'),
        onSaveConfig('path_options.marker_fill_color', preferences.markerFillColor, 'string'),
        onSaveConfig('path_options.marker_fill_opacity', preferences.markerFillOpacity, 'float'),
        onSaveConfig('path_options.marker_border_type', preferences.markerBorderType, 'string'),
        onSaveConfig('path_options.marker_border_color', preferences.markerBorderColor, 'string'),
        onSaveConfig('path_options.marker_border_opacity', preferences.markerBorderOpacity, 'float'),
        onSaveConfig('path_options.marker_border_weight', preferences.markerBorderWeight, 'int'),
        onSaveConfig('path_options.marker_border_dash', preferences.markerBorderDash, 'string'),
        onSaveConfig('path_options.marker_shade_children', preferences.markerShadeChildren, 'bool'),
        onSaveConfig('filters.provider', JSON.stringify(preferences.filterProvider || []), 'string'),
        onSaveConfig('filters.source', JSON.stringify(preferences.filterSource || []), 'string'),
        onSaveConfig('filters.travel_mode', JSON.stringify(preferences.filterTravelMode || []), 'string'),
        onSaveConfig('filters.accuracy_mode', preferences.accuracyMode || 'all', 'string'),
        onSaveConfig('filters.altitude_mode', preferences.altitudeMode || 'all', 'string'),
        onSaveConfig('filters.speed_mode', preferences.speedMode || 'all', 'string'),
        onSaveConfig('filters.bearing_mode', preferences.bearingMode || 'all', 'string'),
        onSaveConfig('filters.velocity_mode', preferences.velocityMode || 'all', 'string'),
        onSaveConfig('filters.acceleration_mode', preferences.accelerationMode || 'all', 'string'),
        onSaveConfig('styling.source', preferences.stylingSource || 'travel_mode', 'string'),
        onSaveConfig('styling.category_id', preferences.stylingCategoryId != null ? String(preferences.stylingCategoryId) : '', 'string'),
        onSaveConfig('styling.category_level', String(preferences.stylingCategoryLevel ?? 'leaf'), 'string')
      ]);
    } catch (error: any) {
      setSaveError(error.message || 'Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Accordion key="Styling" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ColorLensIcon fontSize="small" />
            <Typography sx={{ ml: 1 }}>Styling source</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            <FormControl size="small" fullWidth>
              <InputLabel>Source</InputLabel>
              <Select
                label="Source"
                value={preferences.stylingSource ?? 'travel_mode'}
                onChange={(e) => setPreferences({ ...preferences, stylingSource: e.target.value })}
              >
                <MenuItem value="travel_mode">Travel mode</MenuItem>
                <MenuItem value="tag_category">Tag category</MenuItem>
                <MenuItem value="none">None (defaults only)</MenuItem>
              </Select>
            </FormControl>
            {preferences.stylingSource === 'tag_category' && (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={preferences.stylingCategoryId ?? ''}
                    onChange={(e) => setPreferences({
                      ...preferences,
                      stylingCategoryId: e.target.value === '' ? null : Number(e.target.value),
                    })}
                  >
                    <MenuItem value="">— select —</MenuItem>
                    {tagsCtx.registry.effectiveCategories.map(c => (
                      <MenuItem key={c.id} value={c.id}>{c.tag_category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Level</InputLabel>
                  <Select
                    label="Level"
                    value={preferences.stylingCategoryLevel ?? 'leaf'}
                    onChange={(e) => setPreferences({ ...preferences, stylingCategoryLevel: e.target.value })}
                  >
                    <MenuItem value={0}>Root</MenuItem>
                    <MenuItem value={1}>Level 1</MenuItem>
                    <MenuItem value={2}>Level 2</MenuItem>
                    <MenuItem value={3}>Level 3</MenuItem>
                    <MenuItem value="leaf">Leaf (assigned tag)</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion key="Smoothing" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {Icons.drawer.content["smoothing"]}
            <Typography sx={{ ml: 1 }}>Smoothing</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Slider
            value={preferences.smoothingFactor}
            onChange={(_, v) => handleSmoothingChange(v as number)}
            min={0}
            max={1}
            step={0.01}
            valueLabelDisplay="auto"
          />
        </AccordionDetails>
      </Accordion>

      <Accordion key="Paths" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {Icons.drawer.content["paths"]}
            <Typography sx={{ ml: 1 }}>Paths</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={preferences.pathType}
                label="Type"
                onChange={(e) => handlePathTypeChange(e.target.value)}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="solid">Solid</MenuItem>
                <MenuItem value="dashed">Dashed</MenuItem>
                <MenuItem value="dotted">Dotted</MenuItem>
                <MenuItem value="dash-dot">Dash-Dot</MenuItem>
              </Select>
            </FormControl>

            {preferences.pathType !== 'none' && (
              <>
                <ColorPicker
                  label="Color"
                  dialogTitle="Path Color"
                  value={preferences.pathColor}
                  onChange={handlePathColorChange}
                />

                <TextField
                  type="number"
                  label="Opacity"
                  value={preferences.pathOpacity}
                  onChange={(e) => handlePathOpacityChange(parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0, max: 1, step: 0.1 }}
                  fullWidth
                />

                <TextField
                  type="number"
                  label="Weight"
                  value={preferences.pathWeight}
                  onChange={(e) => handlePathWeightChange(parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1, max: 20 }}
                  fullWidth
                />

                {preferences.pathType === 'dashed' && (
                  <TextField
                    type="text"
                    label="Dash Pattern"
                    value={preferences.pathDash}
                    onChange={(e) => handlePathDashChange(e.target.value)}
                    placeholder="5,5"
                    fullWidth
                  />
                )}
              </>
            )}
            <FormControlLabel
              control={<Switch checked={!!preferences.pathShadeChildren} onChange={(e) => handlePathShadeChildrenChange(e.target.checked)} />}
              label="Shade Children"
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion key="Markers" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {Icons.drawer.content["markers"]}
            <Typography sx={{ ml: 1 }}>Markers</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={preferences.markerType}
                label="Type"
                onChange={(e) => handleMarkerTypeChange(e.target.value)}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="circle">Circle</MenuItem>
                <MenuItem value="square">Square</MenuItem>
                <MenuItem value="triangle">Triangle</MenuItem>
                <MenuItem value="diamond">Diamond</MenuItem>
              </Select>
            </FormControl>

            {preferences.markerType !== 'none' && (
              <>
                <TextField
                  type="number"
                  label="Radius"
                  value={preferences.markerRadius}
                  onChange={(e) => handleMarkerRadiusChange(parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1, max: 50 }}
                  fullWidth
                />

                <FormControl fullWidth>
                  <InputLabel>Fill Type</InputLabel>
                  <Select
                    value={preferences.markerFillType}
                    label="Fill Type"
                    onChange={(e) => handleMarkerFillTypeChange(e.target.value)}
                  >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="solid">Solid</MenuItem>
                  </Select>
                </FormControl>

                {preferences.markerFillType !== 'none' && (
                  <>
                    <ColorPicker
                      label="Fill Color"
                      dialogTitle="Marker Fill Color"
                      value={preferences.markerFillColor}
                      onChange={handleMarkerFillColorChange}
                    />

                    <TextField
                      type="number"
                      label="Fill Opacity"
                      value={preferences.markerFillOpacity}
                      onChange={(e) => handleMarkerFillOpacityChange(parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, max: 1, step: 0.1 }}
                      fullWidth
                    />
                  </>
                )}

                <FormControl fullWidth>
                  <InputLabel>Border Type</InputLabel>
                  <Select
                    value={preferences.markerBorderType}
                    label="Border Type"
                    onChange={(e) => handleMarkerBorderTypeChange(e.target.value)}
                  >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="solid">Solid</MenuItem>
                    <MenuItem value="dashed">Dashed</MenuItem>
                  </Select>
                </FormControl>

                {preferences.markerBorderType !== 'none' && (
                  <>
                    <ColorPicker
                      label="Border Color"
                      dialogTitle="Marker Border Color"
                      value={preferences.markerBorderColor}
                      onChange={handleMarkerBorderColorChange}
                    />

                    <TextField
                      type="number"
                      label="Border Opacity"
                      value={preferences.markerBorderOpacity}
                      onChange={(e) => handleMarkerBorderOpacityChange(parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, max: 1, step: 0.1 }}
                      fullWidth
                    />

                    <TextField
                      type="number"
                      label="Border Weight"
                      value={preferences.markerBorderWeight}
                      onChange={(e) => handleMarkerBorderWeightChange(parseInt(e.target.value) || 1)}
                      inputProps={{ min: 1, max: 20 }}
                      fullWidth
                    />

                    {preferences.markerBorderType === 'dashed' && (
                      <TextField
                        type="text"
                        label="Border Dash Pattern"
                        value={preferences.markerBorderDash}
                        onChange={(e) => handleMarkerBorderDashChange(e.target.value)}
                        placeholder="5,5"
                        fullWidth
                      />
                    )}
                  </>
                )}
              </>
            )}
            <FormControlLabel
              control={<Switch checked={!!preferences.markerShadeChildren} onChange={(e) => handleMarkerShadeChildrenChange(e.target.checked)} />}
              label="Shade Children"
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion key="DefaultFilters" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {Icons.drawer.content["filters"] || Icons.drawer.content["filter"]}
            <Typography sx={{ ml: 1 }}>Default Filters</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Providers</Typography>
              <FormGroup>
                {['gps', 'fused', 'passive', 'network'].map((provider) => (
                  <FormControlLabel
                    key={provider}
                    control={
                      <Checkbox
                        checked={(preferences.filterProvider || []).includes(provider)}
                        onChange={(e) => handleFilterProviderChange(provider, e.target.checked)}
                      />
                    }
                    label={provider.charAt(0).toUpperCase() + provider.slice(1)}
                  />
                ))}
              </FormGroup>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Sources</Typography>
              <FormGroup>
                {['manual', 'automatic'].map((source) => (
                  <FormControlLabel
                    key={source}
                    control={
                      <Checkbox
                        checked={(preferences.filterSource || []).includes(source)}
                        onChange={(e) => handleFilterSourceChange(source, e.target.checked)}
                      />
                    }
                    label={source.charAt(0).toUpperCase() + source.slice(1)}
                  />
                ))}
              </FormGroup>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Travel Modes</Typography>
              <FormGroup>
                {['walking', 'running', 'driving', 'cycling', 'stationary'].map((mode) => (
                  <FormControlLabel
                    key={mode}
                    control={
                      <Checkbox
                        checked={(preferences.filterTravelMode || []).includes(mode)}
                        onChange={(e) => handleFilterTravelModeChange(mode, e.target.checked)}
                      />
                    }
                    label={mode.charAt(0).toUpperCase() + mode.slice(1)}
                  />
                ))}
              </FormGroup>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Numeric Filters</Typography>
              <Stack spacing={2}>
                {[
                  { key: 'accuracy', label: 'Accuracy' },
                  { key: 'altitude', label: 'Altitude' },
                  { key: 'speed', label: 'Speed' },
                  { key: 'bearing', label: 'Bearing' },
                  { key: 'velocity', label: 'Velocity' },
                  { key: 'acceleration', label: 'Acceleration' }
                ].map(({ key, label }) => (
                  <Box key={key}>
                    <Typography variant="body2" sx={{ mb: 1 }}>{label}</Typography>
                    <RadioGroup
                      value={preferences[`${key}Mode`] || 'all'}
                      onChange={(e) => handleNumericFilterModeChange(key, e.target.value)}
                      row
                    >
                      <FormControlLabel value="all" control={<Radio />} label="All" />
                      <FormControlLabel value="onlyValues" control={<Radio />} label="Only Values" />
                      <FormControlLabel value="onlyNull" control={<Radio />} label="Only Undefined" />
                    </RadioGroup>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Button
        variant="contained"
        onClick={handleSavePreferences}
        disabled={isSaving}
      >
        {isSaving ? <CircularProgress size={24} /> : 'Save Preferences'}
      </Button>
      {saveError && (
        <Typography color="error" sx={{ fontSize: '0.875rem' }}>
          {saveError}
        </Typography>
      )}
    </Box>
  );
};

export default MapPreferences;
